import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check, Download, FileText, ShieldCheck } from "lucide-react";
import SEO from "@/components/SEO";
import SpecTable from "@/components/SpecTable";
import ProductCard from "@/components/ProductCard";
import CTASection from "@/components/CTASection";
import { Reveal } from "@/components/Reveal";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { getProduct } from "@/data/products";
import { useCatalog } from "@/lib/catalog";
import { track } from "@/lib/api";
import NotFound from "@/pages/NotFound";

export default function ProductDetail() {
  const { id } = useParams();
  const { products } = useCatalog();
  const product = products.find((p) => p.id === id) || getProduct(id);

  useEffect(() => {
    if (product) track("product_view", { id: product.id, name: product.name });
  }, [product]);

  if (!product) return <NotFound />;

  const related = products.filter((p) => p.category === product.category && p.id !== product.id).slice(0, 3);

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: `Amaya Pro ${product.name}`,
    sku: product.orderNumber,
    description: product.tagline,
    image: `https://amaya.energy${product.image}`,
    brand: { "@type": "Brand", name: "Amaya Pro" },
    manufacturer: { "@type": "Organization", name: "Amaya Pro" },
  };

  return (
    <div className="bg-bone min-h-screen" data-testid="product-detail-page">
      <SEO title={`${product.name} (${product.orderNumber})`} description={product.tagline} path={`/products/${product.id}`} type="product" jsonLd={jsonLd} />

      <section className="pt-28 pb-16 bg-bone">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <nav className="flex items-center gap-2 text-xs text-zinc-500 mb-10 font-mono" aria-label="Breadcrumb" data-testid="breadcrumb">
            <Link to="/products" className="hover:text-signal flex items-center gap-1"><ArrowLeft size={12} /> Products</Link>
            <span>/</span>
            <span>{product.category}</span>
            <span>/</span>
            <span className="text-ink">{product.orderNumber}</span>
          </nav>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <Reveal className="lg:sticky lg:top-24 self-start">
              <div className={`relative border border-zinc-200 ${product.darkImage ? "spotlight-dark" : "spotlight"} h-[380px] md:h-[460px] flex items-center justify-center overflow-hidden`}>
                <img src={product.image} alt={`${product.name} — order ${product.orderNumber}`} className="max-h-[75%] w-auto object-contain" data-testid="product-image" />
                <span className="absolute top-4 left-4 font-mono text-[10px] uppercase tracking-widest text-zinc-500 bg-white/85 px-2 py-1">{product.category}</span>
              </div>
              {product.compliance && (
                <div className="mt-4 bg-white border border-zinc-200 p-4 flex flex-wrap gap-x-6 gap-y-2" data-testid="compliance-strip">
                  {product.compliance.map((c) => (
                    <span key={c} className="flex items-center gap-1.5 text-xs text-zinc-600">
                      <ShieldCheck size={13} className="text-signal" /> {c}
                    </span>
                  ))}
                </div>
              )}
            </Reveal>

            <Reveal delay={0.1}>
              <p className="font-mono text-xs text-signal uppercase tracking-widest">Order № {product.orderNumber}</p>
              <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-ink mt-3 leading-[1.08]" data-testid="product-name">
                {product.name}
              </h1>
              <p className="text-zinc-600 text-base md:text-lg mt-4 leading-relaxed">{product.tagline}</p>

              <div className="flex flex-wrap gap-2 mt-6">
                {product.capabilities.map((c) => (
                  <span key={c} className="text-xs px-2.5 py-1 bg-white border border-zinc-200 text-zinc-600">{c}</span>
                ))}
              </div>

              <div className="flex flex-wrap gap-3 mt-8">
                <Link to="/contact?type=quote" className="px-6 py-3 bg-signal text-white text-sm font-medium hover:bg-[#5c8f23] transition-colors flex items-center gap-2" data-testid="product-quote-btn">
                  Request a Quote <ArrowRight size={15} />
                </Link>
                <Link to="/contact?type=demo" className="px-6 py-3 border border-zinc-300 text-ink text-sm font-medium hover:border-signal transition-colors" data-testid="product-demo-btn">
                  Request a Demo
                </Link>
              </div>

              <Tabs defaultValue="overview" className="mt-12" data-testid="product-tabs">
                <TabsList className="bg-white border border-zinc-200 rounded-none h-auto p-0 flex-wrap justify-start">
                  {["overview", "features", "specs", "downloads"].map((t) => (
                    <TabsTrigger
                      key={t}
                      value={t}
                      data-testid={`tab-${t}`}
                      className="rounded-none px-5 py-2.5 text-sm data-[state=active]:bg-forest data-[state=active]:text-white capitalize"
                    >
                      {t === "specs" ? "Technical Specs" : t}
                    </TabsTrigger>
                  ))}
                </TabsList>
                <TabsContent value="overview" className="pt-6">
                  <ul className="space-y-2.5" data-testid="overview-list">
                    {product.overview.map((o) => (
                      <li key={o} className="flex items-start gap-2.5 text-sm text-zinc-700">
                        <Check size={15} className="text-signal shrink-0 mt-0.5" /> {o}
                      </li>
                    ))}
                  </ul>
                  <p className="text-sm text-zinc-500 mt-6 leading-relaxed border-l-2 border-signal pl-4">
                    Part of the Amaya Pro System — Web & App: mobile app, desktop-friendly tools and an online portal for pre-planning, pre & post programming, location controls and energy data.
                  </p>
                </TabsContent>
                <TabsContent value="features" className="pt-6">
                  <ul className="space-y-2.5" data-testid="features-list">
                    {product.features.map((f) => (
                      <li key={f} className="flex items-start gap-2.5 text-sm text-zinc-700">
                        <Check size={15} className="text-signal shrink-0 mt-0.5" /> {f}
                      </li>
                    ))}
                  </ul>
                </TabsContent>
                <TabsContent value="specs" className="pt-6">
                  <SpecTable specs={product.specs} />
                  <p className="text-xs text-zinc-400 mt-3">
                    Fields marked “See datasheet” are being verified against the manufacturer specification and will be published once confirmed.
                  </p>
                </TabsContent>
                <TabsContent value="downloads" className="pt-6">
                  <div className="bg-white border border-zinc-200 divide-y divide-zinc-100" data-testid="downloads-list">
                    {["Specification sheet", "Wiring diagram", "Sensor coverage diagram", "Dimensions drawing"].map((d) => (
                      <div key={d} className="flex items-center justify-between px-5 py-4">
                        <span className="flex items-center gap-3 text-sm text-ink">
                          <FileText size={16} className="text-zinc-400" /> {d} — {product.orderNumber}
                        </span>
                        <Link
                          to={`/contact?type=quote&product=${product.id}`}
                          onClick={() => track("datasheet_request", { product: product.id, doc: d })}
                          className="text-xs font-medium text-signal flex items-center gap-1 hover:underline"
                          data-testid={`download-${d.toLowerCase().replace(/ /g, "-")}`}
                        >
                          <Download size={13} /> Request
                        </Link>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-zinc-400 mt-3">Individual PDF datasheets are being prepared for direct download. Request any document and our team will send it.</p>
                </TabsContent>
              </Tabs>
            </Reveal>
          </div>
        </div>
      </section>

      {related.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 md:px-12 pb-24" data-testid="related-products">
          <Reveal>
            <h2 className="font-display text-2xl md:text-3xl font-bold text-ink tracking-tight mb-8">Related {product.category} products</h2>
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {related.map((p, i) => (
              <ProductCard key={p.id} product={p} index={i} />
            ))}
          </div>
        </section>
      )}

      <CTASection title="Need help choosing the right control?" sub="Tell us about your space — we'll spec the right Amaya Pro devices." />
    </div>
  );
}
